// Import ag-grid modules and CSS first to ensure proper loading order
import {
  AllCommunityModule,
  ClientSideRowModelModule,
  ModuleRegistry,
  provideGlobalGridOptions,
  themeQuartz,
} from 'ag-grid-community'

// Register ag-grid modules immediately
ModuleRegistry.registerModules([AllCommunityModule, ClientSideRowModelModule])

import { createTheme, MantineProvider } from '@mantine/core'
import { Notifications } from '@mantine/notifications'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { createRouter, RouterProvider } from '@tanstack/react-router'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { auth } from './api/auth'
import Pending from './components/layout/pending'
import { FeatureFlagsProvider } from './contexts/FeatureFlagsContext'
import { routeTree } from './routeTree.gen'

import '@mantine/carousel/styles.css'
import '@mantine/charts/styles.css'
import '@mantine/core/styles.css'
import '@mantine/dates/styles.css'
import '@mantine/dropzone/styles.css'
import '@mantine/notifications/styles.css'
import { registerLicense } from '@syncfusion/ej2-base'
import { RowData } from '@tanstack/react-table'
import './main.css'

import { AxiosError } from 'axios'
import dayjs from 'dayjs'
import buddhistEra from 'dayjs/plugin/buddhistEra'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import isoWeek from 'dayjs/plugin/isoWeek'
import weekOfYear from 'dayjs/plugin/weekOfYear'

dayjs.extend(buddhistEra)
dayjs.extend(weekOfYear)
dayjs.extend(customParseFormat)
dayjs.extend(isoWeek)

// Early error detection for critical JavaScript errors
window.addEventListener('error', (event) => {
  const error = event.error
  const message = event.message || ''
  const filename = event.filename || ''

  // Check for React instance errors (multiple React instances)
  if (
    message.includes('useLayoutEffect') ||
    message.includes('Cannot read properties of undefined') ||
    message.includes("reading 'useLayoutEffect'") ||
    message.includes('React') ||
    filename.includes('vendor-') ||
    filename.includes('react-') ||
    error?.stack?.includes('useLayoutEffect') ||
    error?.stack?.includes('Cannot read properties of undefined')
  ) {
    console.error(
      'CRITICAL REACT ERROR - Multiple React instances or chunk loading issue:',
      {
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        error: error,
        stack: error?.stack,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
      }
    )

    // Immediate emergency UI - don't wait for React
    showEmergencyErrorUI(
      'โปรแกรมเกิดข้อผิดพลาด',
      'ระบบไม่สามารถโหลดได้ กรุณารีโหลดหน้าเว็บ หากปัญหายังคงเกิดขึ้น กรุณาติดต่อผู้ดูแลระบบ'
    )
    return
  }

  // Log other critical errors
  console.error('Critical JavaScript error:', {
    message: event.message,
    filename: event.filename,
    lineno: event.lineno,
    colno: event.colno,
    error: error,
    timestamp: new Date().toISOString(),
  })
})

// Also catch unhandled promise rejections from chunk loading
window.addEventListener('unhandledrejection', (event) => {
  const error = event.reason
  const message = error?.message || ''

  // Check for React-related Promise rejections
  if (
    message.includes('useLayoutEffect') ||
    message.includes('Cannot read properties of undefined') ||
    message.includes('React')
  ) {
    console.error('React error in Promise rejection:', error)
    showEmergencyErrorUI(
      'โปรแกรมเกิดข้อผิดพลาด',
      'ระบบไม่สามารถโหลดได้ กรุณารีโหลดหน้าเว็บ'
    )
    event.preventDefault()
    return
  }

  // Existing chunk loading error handling
  if (
    error?.message?.includes('Loading chunk') ||
    error?.message?.includes('Failed to import') ||
    error?.name === 'ChunkLoadError'
  ) {
    console.warn('Chunk loading failed, will retry on next navigation:', error)
    event.preventDefault()

    if (
      typeof window !== 'undefined' &&
      'navigator' in window &&
      navigator.onLine
    ) {
      console.info(
        'Network appears to be online, chunk will retry on next action'
      )
    } else {
      console.warn(
        'Network appears to be offline, chunk will retry when connection is restored'
      )
    }
  }
})

// Emergency error UI for critical pre-React errors
function showEmergencyErrorUI(title: string, message: string) {
  const root = document.getElementById('root')
  if (root) {
    root.innerHTML = `
      <div style="
        display: flex;
        justify-content: center;
        align-items: center;
        min-height: 100vh;
        padding: 2rem;
        background-color: #f8f9fa;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
      ">
        <div style="
          max-width: 500px;
          padding: 2rem;
          background: white;
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
          text-align: center;
        ">
          <div style="
            width: 64px;
            height: 64px;
            margin: 0 auto 1rem;
            background: #ff6b6b;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            color: white;
            font-size: 24px;
            font-weight: bold;
          ">!</div>

          <h2 style="
            color: #333;
            margin: 0 0 1rem;
            font-size: 1.5rem;
            font-weight: 600;
          ">${title}</h2>

          <p style="
            color: #666;
            margin: 0 0 2rem;
            line-height: 1.5;
          ">${message}</p>

          <button onclick="window.location.reload()" style="
            background: #228be6;
            color: white;
            border: none;
            padding: 12px 24px;
            border-radius: 6px;
            font-size: 16px;
            font-weight: 500;
            cursor: pointer;
            transition: background-color 0.2s;
          " onmouseover="this.style.backgroundColor='#1c7ed6'" onmouseout="this.style.backgroundColor='#228be6'">
            รีโหลดหน้าเว็บ
          </button>
        </div>
      </div>
    `
  }
}

// Handle module loading errors during dynamic imports
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const originalImport = (window as any).__import || (window as any).import
if (originalImport) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ;(window as any).__import = (window as any).import = async (url: string) => {
    try {
      return await originalImport(url)
    } catch (error: unknown) {
      console.warn(`Failed to import module: ${url}`, error)

      // Retry once after a short delay
      const errorWithRetry = error as { __retried?: boolean }
      if (!errorWithRetry.__retried) {
        errorWithRetry.__retried = true
        await new Promise((resolve) => setTimeout(resolve, 1000))
        return await originalImport(url)
      }

      throw error
    }
  }
}

// Registering Syncfusion license key
registerLicense(
  'Ngo9BigBOggjHTQxAR8/V1NMaF5cXmBCf1FpRmJGdld5fUVHYVZUTXxaS00DNHVRdkdnWH1fdnRRRGJYUERzV0s='
)

const defaultStaleTimeInSecond = 60

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: defaultStaleTimeInSecond * 1000,
      retry: (count, error) => {
        console.log('retry', count, error)
        if (error) {
          console.error('queryclient', error)
          const axiosError = error as AxiosError
          const codeStatus = axiosError.response?.status || 0
          if (codeStatus === 403) {
            return false
          }
          if (count >= 3) {
            return false
          }
        }
        return true
      },
    },
  },
})

const appTheme = themeQuartz.withParams({
  spacing: 8,
  accentColor: '#E6F2FD',
  headerTextColor: '#ffffff',
  headerBackgroundColor: '#758694',
  headerColumnBorder: 'solid 1px #DEE2E6',
  headerColumnResizeHandleColor: '#ababab',
  fontFamily: 'sans-serif',
  fontSize: 'var(--mantine-font-size-sm)',
  oddRowBackgroundColor: '#fbfbfb',
  rowHoverColor: '#E6F2FD',
  rangeSelectionBorderColor: '#758694',
  inputFocusBorder: 'solid 1px var(--mantine-primary-color-filled)',
  checkboxCheckedBackgroundColor: 'var(--mantine-primary-color-filled)',
})

// setting global options for aggrid
provideGlobalGridOptions({
  singleClickEdit: true,
  suppressHeaderFocus: true,
  stopEditingWhenCellsLoseFocus: true,
  enableCellTextSelection: true,
  enableBrowserTooltips: true,
  tooltipShowDelay: 0,
  defaultColDef: {
    resizable: false,
    sortable: false,
    suppressMovable: true,
  },
  theme: appTheme,
})

const theme = createTheme({
  /** Put your mantine theme override here */
})
// Create a new router instance
const router = createRouter({
  routeTree,
  defaultPendingComponent: Pending,
  context: {
    queryClient,
    auth: undefined!,
  },
})

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

declare module '@tanstack/table-core' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface TableMeta<TData extends RowData> {
    border?: number
  }
}

declare module '@tanstack/react-table' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    bg?: string | ((value: TValue) => string)
    color?: string | ((value: TValue) => string)
    header?: {
      align?: 'left' | 'center' | 'right'
    }
    rowSpan?: number
    style?: React.CSSProperties
    alignment?: 'start' | 'end' | 'left' | 'right' | 'center'
  }
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <MantineProvider theme={theme}>
      <Notifications />
      <QueryClientProvider client={queryClient}>
        <FeatureFlagsProvider>
          <RouterProvider
            router={router}
            defaultPreload="intent"
            context={{ auth }}
          />
        </FeatureFlagsProvider>
      </QueryClientProvider>
    </MantineProvider>
  </StrictMode>
)
